import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const VisualizationTools = ({ queryResults, selectedDataset }) => {
  const [activeChart, setActiveChart] = useState('temporal');
  const [chartConfig, setChartConfig] = useState({
    groupBy: 'country',
    metric: 'mentions',
    timeGranularity: 'daily'
  });

  const chartTypes = [
    { value: 'temporal', label: 'Temporal Trends', icon: 'TrendingUp' },
    { value: 'geographic', label: 'Geographic Distribution', icon: 'Map' },
    { value: 'coordination', label: 'Coordination Patterns', icon: 'GitBranch' },
    { value: 'network', label: 'Network Analysis', icon: 'Share2' },
    { value: 'sentiment', label: 'Sentiment Analysis', icon: 'Heart' },
    { value: 'virality', label: 'Virality Metrics', icon: 'Zap' }
  ];

  const groupByOptions = [
    { value: 'country', label: 'By Country' },
    { value: 'platform', label: 'By Platform' },
    { value: 'theme', label: 'By Theme' },
    { value: 'source', label: 'By Source' },
    { value: 'language', label: 'By Language' }
  ];

  const metricOptions = [
    { value: 'mentions', label: 'Mention Count' },
    { value: 'engagement', label: 'Engagement Rate' },
    { value: 'reach', label: 'Reach Metrics' },
    { value: 'virality', label: 'Virality Score' },
    { value: 'confidence', label: 'Confidence Score' }
  ];

  const timeGranularityOptions = [
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  // Mock data for different chart types
  const temporalData = [
    { date: '2024-09-01', mentions: 1247, engagement: 3456, virality: 0.67 },
    { date: '2024-09-02', mentions: 1589, engagement: 4123, virality: 0.72 },
    { date: '2024-09-03', mentions: 2134, engagement: 5678, virality: 0.84 },
    { date: '2024-09-04', mentions: 1876, engagement: 4987, virality: 0.79 },
    { date: '2024-09-05', mentions: 2456, engagement: 6234, virality: 0.91 },
    { date: '2024-09-06', mentions: 2789, engagement: 7123, virality: 0.95 },
    { date: '2024-09-07', mentions: 2234, engagement: 5987, virality: 0.88 },
    { date: '2024-09-08', mentions: 1987, engagement: 5234, virality: 0.82 },
    { date: '2024-09-09', mentions: 2345, engagement: 6456, virality: 0.89 },
    { date: '2024-09-10', mentions: 2678, engagement: 7234, virality: 0.93 }
  ];

  const geographicData = [
    { country: 'Germany', mentions: 3456, engagement: 12456, code: 'DE' },
    { country: 'France', mentions: 2987, engagement: 10234, code: 'FR' },
    { country: 'Italy', mentions: 2456, engagement: 8976, code: 'IT' },
    { country: 'Spain', mentions: 2134, engagement: 7654, code: 'ES' },
    { country: 'Poland', mentions: 1876, engagement: 6543, code: 'PL' },
    { country: 'Netherlands', mentions: 1654, engagement: 5432, code: 'NL' },
    { country: 'Belgium', mentions: 1432, engagement: 4321, code: 'BE' },
    { country: 'Austria', mentions: 1234, engagement: 3987, code: 'AT' }
  ];

  const coordinationData = [
    { theme: 'Health Misinformation', coordination: 0.89, countries: 12, timeWindow: 24 },
    { theme: 'Election Integrity', coordination: 0.76, countries: 8, timeWindow: 48 },
    { theme: 'Climate Denial', coordination: 0.82, countries: 15, timeWindow: 36 },
    { theme: 'Economic Manipulation', coordination: 0.71, countries: 6, timeWindow: 72 },
    { theme: 'Migration Narratives', coordination: 0.94, countries: 18, timeWindow: 12 },
    { theme: 'EU Skepticism', coordination: 0.68, countries: 9, timeWindow: 96 }
  ];

  const sentimentData = [
    { name: 'Positive', value: 23, color: '#059669' },
    { name: 'Neutral', value: 45, color: '#64748B' },
    { name: 'Negative', value: 32, color: '#DC2626' }
  ];

  const viralityData = [
    { platform: 'Facebook', virality: 0.87, reach: 2456789, engagement: 0.034 },
    { platform: 'Twitter', virality: 0.92, reach: 1876543, engagement: 0.056 },
    { platform: 'Instagram', virality: 0.78, reach: 1234567, engagement: 0.042 },
    { platform: 'TikTok', virality: 0.95, reach: 3456789, engagement: 0.078 },
    { platform: 'YouTube', virality: 0.73, reach: 987654, engagement: 0.029 },
    { platform: 'LinkedIn', virality: 0.45, reach: 456789, engagement: 0.018 }
  ];

  const networkData = [
    { source: 'Node A', target: 'Node B', strength: 0.8, type: 'coordination' },
    { source: 'Node B', target: 'Node C', strength: 0.6, type: 'amplification' },
    { source: 'Node C', target: 'Node D', strength: 0.9, type: 'coordination' },
    { source: 'Node A', target: 'Node D', strength: 0.7, type: 'cross-reference' },
    { source: 'Node B', target: 'Node E', strength: 0.5, type: 'amplification' }
  ];

  const renderChart = () => {
    switch (activeChart) {
      case 'temporal':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={temporalData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="date" 
                stroke="#64748B"
                fontSize={12}
                tickFormatter={(value) => new Date(value)?.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}
              />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                labelFormatter={(value) => new Date(value)?.toLocaleDateString('en-GB')}
              />
              <Line 
                type="monotone" 
                dataKey="mentions" 
                stroke="#1E3A8A" 
                strokeWidth={2}
                dot={{ fill: '#1E3A8A', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#1E3A8A', strokeWidth: 2 }}
              />
              <Line 
                type="monotone" 
                dataKey="engagement" 
                stroke="#DC2626" 
                strokeWidth={2}
                dot={{ fill: '#DC2626', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#DC2626', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'geographic':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={geographicData} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" stroke="#64748B" fontSize={12} />
              <YAxis 
                type="category" 
                dataKey="country" 
                stroke="#64748B" 
                fontSize={12}
                width={80}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Bar dataKey="mentions" fill="#1E3A8A" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'coordination':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={coordinationData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="theme" 
                stroke="#64748B" 
                fontSize={12}
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value, name) => [
                  name === 'coordination' ? `${(value * 100)?.toFixed(1)}%` : value,
                  name === 'coordination' ? 'Coordination Score' : 
                  name === 'countries' ? 'Countries Involved' : 'Time Window (hours)'
                ]}
              />
              <Bar dataKey="coordination" fill="#DC2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'sentiment':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={sentimentData}
                cx="50%"
                cy="50%"
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
              >
                {sentimentData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'virality':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={viralityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="platform" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFFFFF', 
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value, name) => [
                  name === 'virality' ? `${(value * 100)?.toFixed(1)}%` : 
                  name === 'engagement' ? `${(value * 100)?.toFixed(2)}%` :
                  new Intl.NumberFormat('en-EU')?.format(value),
                  name === 'virality' ? 'Virality Score' : 
                  name === 'engagement' ? 'Engagement Rate' : 'Reach'
                ]}
              />
              <Bar dataKey="virality" fill="#059669" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'network':
        return (
          <div className="flex items-center justify-center h-96 bg-muted/20 rounded-lg">
            <div className="text-center">
              <Icon name="Share2" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Network Visualization</h3>
              <p className="text-muted-foreground mb-4">
                Interactive network analysis requires specialized visualization libraries
              </p>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div>• {networkData?.length} connections identified</div>
                <div>• 5 primary nodes detected</div>
                <div>• Average connection strength: 0.72</div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Visualization Tools</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Interactive data visualization and pattern analysis
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              onClick={() => {
                // Export functionality
              }}
            >
              Export Chart
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {chartTypes?.map((chart) => (
            <button
              key={chart?.value}
              onClick={() => setActiveChart(chart?.value)}
              className={`
                flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-smooth
                ${activeChart === chart?.value
                  ? 'bg-primary text-primary-foreground shadow-soft'
                  : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                }
              `}
            >
              <Icon name={chart?.icon} size={16} />
              <span>{chart?.label}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label="Group By"
            options={groupByOptions}
            value={chartConfig?.groupBy}
            onChange={(value) => setChartConfig(prev => ({ ...prev, groupBy: value }))}
          />
          <Select
            label="Metric"
            options={metricOptions}
            value={chartConfig?.metric}
            onChange={(value) => setChartConfig(prev => ({ ...prev, metric: value }))}
          />
          <Select
            label="Time Granularity"
            options={timeGranularityOptions}
            value={chartConfig?.timeGranularity}
            onChange={(value) => setChartConfig(prev => ({ ...prev, timeGranularity: value }))}
          />
        </div>
      </div>
      <div className="p-6">
        {queryResults ? (
          <div>
            {renderChart()}
            
            <div className="mt-6 p-4 bg-muted/50 rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {new Intl.NumberFormat('en-EU')?.format(queryResults?.totalRecords)}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Records</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {queryResults?.dataPoints}
                  </div>
                  <div className="text-sm text-muted-foreground">Data Points</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {queryResults?.executionTime}ms
                  </div>
                  <div className="text-sm text-muted-foreground">Execution Time</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-success">
                    Active
                  </div>
                  <div className="text-sm text-muted-foreground">Data Stream</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Icon name="BarChart3" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Data Available</h3>
              <p className="text-muted-foreground">
                Execute a query in the Research Sandbox to generate visualizations
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisualizationTools;